import React from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import { FaCalendarAlt, FaUser, FaArrowRight } from 'react-icons/fa';
import 'swiper/css';
import 'swiper/css/navigation';

// Import news images
import nieImage from '../../assets/images/news/nie.jpg';
import lionClubImage from '../../assets/images/news/lion.jpg';
import slassImage from '../../assets/images/news/slass.jpg';
import slass1Image from '../../assets/images/news/slass1.jpg';
import stemKitImage from '../../assets/images/news/stemkit.jpg';

// News data
const newsArticles = [
  {
    id: 1,
    title: 'Workshop at National Education Institute',
    date: 'Dec 02, 2019',
    author: 'Admin',
    excerpt: 'A workshop was conducted for selected young school inventors and their teachers at the NIE',
    image: nieImage,
    link: 'https://www.facebook.com/STEMLabSL/'
  },
  {
    id: 2,
    title: 'Robotics Workshop With LIONs Club',
    date: 'July 27, 2019',
    author: 'Admin',
    excerpt: 'STEM Robotics Workshop was held at Sella Katharagama Maha Vidyalaya.',
    image: lionClubImage,
    link: 'https://www.facebook.com/STEMLabSL/'
  },
  {
    id: 3,
    title: 'SLASS Exhibition at University of Colombo',
    date: 'Dec 13, 2019',
    author: 'Admin',
    excerpt: 'STEM Labs was placed a stall to introduce the STEMTronics Educational Kits.',
    image: slassImage,
    link: 'https://www.facebook.com/STEMLabSL/'
  },
  {
    id: 4,
    title: 'SLASS Exhibition at University of Colombo',
    date: 'Dec 13, 2019',
    author: 'Admin',
    excerpt: 'STEM Labs was placed a stall to inspire Robotics to the school childrens.',
    image: slass1Image,
    link: 'https://www.facebook.com/STEMLabSL/'
  },
  {
    id: 5,
    title: 'STEMTronics Workshop at STEMLab',
    date: 'Dec 26, 2019',
    author: 'Admin',
    excerpt: 'STEMTronics workshop was held at STEMLab to Inspire Electronics.',
    image: stemKitImage,
    link: 'https://www.facebook.com/STEMLabSL/'
  }
];

const NewsSection = () => {
  // Custom CSS for image enhancement
  const imageEnhanceStyle = `
    .news-image-enhance {
      filter: contrast(1.05) saturate(1.1) brightness(1.03);
      object-fit: cover;
      transition: transform 0.5s ease;
      image-rendering: -webkit-optimize-contrast;
      backface-visibility: hidden;
      transform: translateZ(0);
    }
    
    .news-image-container {
      position: relative;
      overflow: hidden;
    }
    
    .news-image-container::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(circle, rgba(0,0,0,0) 50%, rgba(0,0,0,0.07) 100%);
      pointer-events: none;
    }
    
    .news-card {
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      transition: all 0.3s ease;
    }
    
    .news-card:hover {
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    }
  `;

  return (
    <section id="news" className="py-16">
      <style>{imageEnhanceStyle}</style>
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl md:text-3xl font-extrabold mb-4 relative gradient-text animate-float text-center">
            Latest <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 font-extrabold">News</span>
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full animate-pulse mx-auto mb-4"></div>
          
          <Swiper
            modules={[Navigation]}
            spaceBetween={20}
            slidesPerView={1}
            breakpoints={{
              640: {
                slidesPerView: 1,
              },
              768: {
                slidesPerView: 2,
              },
              1024: {
                slidesPerView: 3,
              }
            }}
            navigation
            className="news-swiper mt-6"
          >
            {newsArticles.map((article) => (
              <SwiperSlide key={article.id}>
                <motion.div 
                  className="bg-white rounded-lg shadow-md overflow-hidden h-full hover:shadow-lg transition-shadow duration-300 news-card"
                  whileHover={{ y: -5 }}
                >
                  <div className="relative overflow-hidden h-48 news-image-container">
                    <img 
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110 news-image-enhance"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5 border border-t-0 border-gray-200">
                    <h4 className="font-bold text-lg mb-3">
                      <a href={article.link} target="_blank" rel="noopener noreferrer" className="text-theme-blue hover:text-theme-accent transition-colors">
                        {article.title}
                      </a>
                    </h4>
                    <p className="text-gray-700 text-sm mb-4">{article.excerpt}</p>
                    <div className="flex justify-between items-center mt-4">
                      <a 
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-theme-accent hover:text-theme-blue transition-colors text-sm font-medium"
                      >
                        Read more <FaArrowRight className="ml-1" />
                      </a>
                      <div className="flex items-center text-xs text-gray-500">
                        <span className="flex items-center mr-2">
                          <FaUser className="mr-1 text-theme-accent" /> 
                          {article.author}
                        </span>
                        <span className="flex items-center">
                          <FaCalendarAlt className="mr-1 text-theme-accent" /> 
                          {article.date}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>
      </div>
    </section>
  );
};

export default NewsSection; 